import ReactDOM from "react-dom";
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { PaperBG, PaperGradientBG } from "../kits";
import manifest from "../data/manifest";
import { Helmet } from "react-helmet";
import { aboutSchema } from "./utils/AboutSchema";
import BookingModal from "./BookingModal";
import ModalWrapper from "./utils/ModalWrapper";
import MonoButton from "../kits/DesignKit/MonoButton";
import HowItWorksSmall from "../components/HowItWorks_small";
import { motion, useInView } from "framer-motion";
import { FaCode, FaChalkboardTeacher, FaComments } from "react-icons/fa";
import TechStack, { TechRoughCircle } from "./TechStack";
import TerminalDetailOverlay from "./TerminalDetailOverlay";
import rough from "roughjs/bundled/rough.esm.js";
import MeineGeschichte from "./MeineGeschichte";
import useDesktop from "../hooks/useDesktop";
import trustedCompanies from "../data/trustedCompanies";
import { SERVICE_DETAIL_CONFIG } from "./ServiceTerminal";
import { COMPANY_DETAIL_CONFIG, toCompanyKey } from "./CompanyDetailConfig";

import ContactForm from "./ContactForm";



// Einfacher handgezeichneter Kreis (für Profilbild etc.)
function RoughCircle({ size = 128, color = "#AC8E66", strokeWidth = 1.5, roughness = 0.8 }) {
  const svgRef = useRef(null);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useLayoutEffect(() => {
    if (!isClient || !svgRef.current) return;
    svgRef.current.innerHTML = "";
    const rc = rough.svg(svgRef.current);
    const padding = strokeWidth + 2;
    const node = rc.circle(size / 2, size / 2, size - padding * 2, {
      roughness,
      stroke: color,
      strokeWidth,
      fill: "none",
    });
    svgRef.current.appendChild(node);
  }, [isClient, size, color, strokeWidth, roughness]);

  if (!isClient) return null;

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      className="pointer-events-none"
    />
  );
}

function SectionBadge({ label, className = "" }) {
  return (
    <span
      className={`inline-block border border-[#AC8E66] text-[#AC8E66] text-[0.6rem] px-2 py-[2px] tracking-[0.14em] uppercase font-mono ${className}`}
    >
      {`// ${label}`}
    </span>
  );
}


// Terminal lines for About hero
const aboutLines = [
  { type: "cmd", text: "$ whoami" },
  { type: "out", text: "> denis_bitter" },
  { type: "out", text: "> software architect · full-stack · dozent" },
  { type: "out", text: "> hamburg" },
  { type: "cmd", text: "$ cat mission.txt" },
  { type: "out", text: "> gute technologie muss verstanden werden." },
  { type: "out", text: "> nicht nur funktionieren." },
];

const valuesLines = [
  { type: "cmd", text: "$ cat values.txt" },
  { type: "out", text: "> Klarheit statt Komplexität" },
  { type: "hint", text: "Systeme, die jeder versteht" },
  { type: "out", text: "> Menschen befähigen, nicht abhängig machen" },
  { type: "hint", text: "Teams, die selbst weiterkommen" },
  { type: "out", text: "> Langfristig statt kurzfristig denken" },
  { type: "hint", text: "Lösungen, die Bestand haben" },
];

const ABOUT_TERMINAL_STORAGE_KEY = "aboutTerminalTyped:v1";

const Terminal = ({ lines, tab, delay = 0, animate = true }) => {
  const [currentLine, setCurrentLine] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [started, setStarted] = useState(delay === 0 || !animate);
  const done = !animate || currentLine >= lines.length;

  useEffect(() => {
    if (!animate) {
      setStarted(true);
      setCurrentLine(lines.length);
      setCharIndex(0);
    }
  }, [animate, lines.length]);

  useEffect(() => {
    if (!animate) return;
    if (delay === 0) return;
    const t = setTimeout(() => setStarted(true), delay);
    return () => clearTimeout(t);
  }, [animate, delay]);

  useEffect(() => {
    if (!animate) return;
    if (!started || done) return;
    const line = lines[currentLine];
    if (charIndex < line.text.length) {
      const speed = line.type === "cmd" ? 50 : 25;
      const t = setTimeout(() => setCharIndex((c) => c + 1), speed);
      return () => clearTimeout(t);
    }
    const nextIsCmdOrEnd = currentLine + 1 >= lines.length || lines[currentLine + 1].type === "cmd";
    const pause = nextIsCmdOrEnd ? 400 : 150;
    const t = setTimeout(() => {
      setCurrentLine((l) => l + 1);
      setCharIndex(0);
    }, pause);
    return () => clearTimeout(t);
  }, [animate, started, currentLine, charIndex, done, lines]);

  return (
    <div className="max-w-2xl mx-auto rounded-lg border border-[#AC8E66]/20
    overflow-hidden shadow-2xl shadow-black/40 text-left">
      <div className="flex items-center gap-2 px-4 py-2
       bg-[#1a1a1a] border-b border-[#AC8E66]/10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
        <span className="ml-2 text-xs text-[#AC8E66] select-none font-mono">{tab}</span>
      </div>
      <div className="px-4 py-4 bg-[#0F0F0F] font-mono space-y-1 tracking-[0.01em]">
        {!started && (
          <p className="text-sm">
            <span className="inline-block w-[2px] h-4 bg-[#AC8E66] align-middle animate-cursor-blink" />
          </p>
        )}
        {started && lines.map((line, i) => {
          const cursor = (
            <span className="inline-block w-[2px] h-4 bg-[#AC8E66] ml-0.5 align-middle animate-cursor-blink" />
          );

          if (!animate) {
            if (line.type === "cmd") return (
              <p key={i} className="text-sm mt-3 first:mt-0 text-[#EFEBDC]/60">
                <span className="text-[#AC8E66]">$</span>{line.text.slice(1)}
              </p>
            );
            if (line.type === "hint") return (
              <p key={i} className="text-sm text-[#CEAA7E] pl-4">{line.text}</p>
            );
            return (
              <p key={i} className="mt-1 flex items-start gap-2 text-sm text-[#EFEBDC]">
                <span className="text-[#AC8E66]/70 flex-none">&gt;</span>
                <span className="min-w-0 flex-1">{line.text.slice(1)}</span>
              </p>
            );
          }

          if (i > currentLine) return null;
          const isTyping = i === currentLine && !done;
          const displayed = isTyping ? line.text.slice(0, charIndex) : line.text;

          if (line.type === "cmd") {
            if (!displayed && isTyping) return (
              <p key={i} className="text-sm mt-3 first:mt-0">{cursor}</p>
            );
            return (
              <p key={i} className="text-sm mt-3 first:mt-0 text-[#EFEBDC]/60">
                <span className="text-[#AC8E66]">$</span>{displayed.slice(1)}{isTyping && cursor}
              </p>
            );
          }
          if (line.type === "hint") {
            if (!displayed && isTyping) return (
              <p key={i} className="text-sm text-[#CEAA7E] pl-4">{cursor}</p>
            );
            return (
              <p key={i} className="text-sm text-[#CEAA7E] pl-4">
                {displayed}{isTyping && cursor}
              </p>
            );
          }
          if (!displayed && isTyping) return (
            <p key={i} className="mt-1 flex items-start gap-2 text-sm">{cursor}</p>
          );
          return (
            <p key={i} className="mt-1 flex items-start gap-2 text-sm text-[#EFEBDC]">
              <span className="text-[#AC8E66]/70 flex-none">&gt;</span>
              <span className="min-w-0 flex-1">{displayed.slice(1)}{isTyping && cursor}</span>
            </p>
          );
        })}
      </div>
    </div>
  );
};

const About = () => {
  const isDesktop = useDesktop();
  const [aboutTerminalTyped] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.sessionStorage.getItem(ABOUT_TERMINAL_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [showBooking, setShowBooking] = useState(false);
  const [activeServiceDetailId, setActiveServiceDetailId] = useState(null);
  const [activeCompanyKey, setActiveCompanyKey] = useState(null);
  const location = useLocation();
  const introRef = useRef(null);
  const introInView = useInView(introRef, { once: true });
  const pillars = [
    {
      id: "entwicklung",
      title: "Entwicklung",
      icon: FaCode,
      highlight: "React · Angular · Flutter · three.js · System Architecture",
      detail: "Von der Idee zur skalierbaren Lösung",
    },
    {
      id: "training",
      title: "Training",
      icon: FaChalkboardTeacher,
      highlight: "Unity · Maya · Web-Technologien · Team-Workshops",
      detail: "Wissen, das im Alltag wirkt",
    },
    {
      id: "beratung",
      title: "Beratung",
      icon: FaComments,
      highlight: "Architektur-Reviews · Code-Audits · Strategie",
      detail: "Klarheit für komplexe Entscheidungen",
    },
  ];
  const orderedServiceIds = pillars
    .map((pillar) => pillar.id)
    .filter((id, index, arr) => arr.indexOf(id) === index && Boolean(SERVICE_DETAIL_CONFIG[id]));
  const activeServiceIndex = activeServiceDetailId
    ? orderedServiceIds.indexOf(activeServiceDetailId)
    : -1;
  const activeServicePillar = activeServiceDetailId
    ? pillars.find((pillar) => pillar.id === activeServiceDetailId) || null
    : null;
  const activeServiceDetail = activeServiceDetailId
    ? SERVICE_DETAIL_CONFIG[activeServiceDetailId]
    : null;
  const canOpenServiceDetail = (pillarId) => Boolean(SERVICE_DETAIL_CONFIG[pillarId]);
  const openServiceDetail = (pillarId) => {
    if (!canOpenServiceDetail(pillarId)) return;
    setActiveServiceDetailId(pillarId);
  };
  const handleServicePillarClick = (pillarId, event) => {
    openServiceDetail(pillarId);
    // Safari keeps focus outline after mouse click; blur only for pointer clicks.
    if (event?.detail > 0 && event.currentTarget instanceof HTMLElement) {
      event.currentTarget.blur();
    }
  };
  const closeServiceDetail = () => setActiveServiceDetailId(null);
  const prevServiceDetail = () => {
    if (activeServiceIndex <= 0) return;
    setActiveServiceDetailId(orderedServiceIds[activeServiceIndex - 1]);
  };
  const nextServiceDetail = () => {
    if (activeServiceIndex < 0 || activeServiceIndex >= orderedServiceIds.length - 1) return;
    setActiveServiceDetailId(orderedServiceIds[activeServiceIndex + 1]);
  };
  const orderedCompanyKeys = trustedCompanies
    .map((company) => toCompanyKey(company.name))
    .filter((key, index, arr) => arr.indexOf(key) === index && Boolean(COMPANY_DETAIL_CONFIG[key]));
  const activeCompanyIndex = activeCompanyKey ? orderedCompanyKeys.indexOf(activeCompanyKey) : -1;
  const activeCompanyDetail = activeCompanyKey ? COMPANY_DETAIL_CONFIG[activeCompanyKey] : null;
  const openCompanyDetail = (companyName) => {
    const key = toCompanyKey(companyName);
    if (!COMPANY_DETAIL_CONFIG[key]) return;
    setActiveCompanyKey(key);
  };
  const prevCompanyDetail = () => {
    if (activeCompanyIndex <= 0) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex - 1]);
  };
  const nextCompanyDetail = () => {
    if (activeCompanyIndex < 0 || activeCompanyIndex >= orderedCompanyKeys.length - 1) return;
    setActiveCompanyKey(orderedCompanyKeys[activeCompanyIndex + 1]);
  };

  useEffect(() => {
    if (!location.hash) return;
    const hashId = decodeURIComponent(location.hash.replace("#", "").trim());
    if (!hashId) return;

    const alignToHash = (behavior = "auto") => {
      const el = document.getElementById(hashId);
      if (!el) return false;
      const navbarHeightRaw = getComputedStyle(document.documentElement).getPropertyValue("--navbar-height");
      const navbarHeight = parseInt(navbarHeightRaw, 10) || 0;
      const offset = navbarHeight + 16;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior });
      return true;
    };

    // first attempt (smooth), then re-align a few times after layout shifts
    alignToHash("smooth");
    const t1 = setTimeout(() => alignToHash("auto"), 250);
    const t2 = setTimeout(() => alignToHash("auto"), 700);
    const t3 = setTimeout(() => alignToHash("auto"), 1200);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [location.hash]);

  useEffect(() => {
    if (!aboutTerminalTyped || typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(ABOUT_TERMINAL_STORAGE_KEY, "1");
    } catch {
      // ignore storage errors
    }
  }, [aboutTerminalTyped]);

  useEffect(() => {
    if (!isDesktop || aboutTerminalTyped || typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(ABOUT_TERMINAL_STORAGE_KEY, "1");
    } catch {
      // ignore storage errors
    }
  }, [isDesktop, aboutTerminalTyped]);

  const shouldAnimateAboutTerminal = isDesktop && !aboutTerminalTyped;

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      {/* ========== INTRO - Der Mensch ========== */}
      <PaperGradientBG 
      variant="zenBlack"
      
      >
        
      <section className="relative text-[#EFEBDC] overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-28">
          <motion.div
            ref={introRef}
            initial={{ opacity: 0, y: 10 }}
            animate={introInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
            transition={{ duration: 0.9 }}
            className="text-center"
          >
            {/* Foto/Avatar */}
            <div className="relative w-32 h-32 md:w-40 md:h-40 mx-auto mb-8">
              {/* RoughCircle für Mobile */}
              <div className="absolute inset-0 flex items-center justify-center md:hidden">
                <RoughCircle size={128} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              {/* RoughCircle für Desktop */}
              <div className="absolute inset-0 items-center justify-center hidden md:flex">
                <RoughCircle size={160} color="#AC8E66" strokeWidth={1.5} roughness={0.8} />
              </div>
              <div className="absolute inset-2 rounded-full bg-gradient-to-br from-brand to-brand flex items-center justify-center overflow-hidden">
                <img
                  src="/images/den.png"
                  width={256}
                  height={256}
                  alt="Denis Bitter"
                  className="w-full h-full object-cover object-[80%_20%]"
                />
              </div>
            </div>

            {/* Terminals */}
            <div className="space-y-4">
              <Terminal lines={aboutLines} tab="about" animate={shouldAnimateAboutTerminal} />
              <Terminal
                lines={valuesLines}
                tab="values"
                delay={2500}
                animate={shouldAnimateAboutTerminal}
              />
            </div>

          </motion.div>
        </div>
      </section>
      </PaperGradientBG>

      {/* ========== KAPITEL - Die Story ========== */}
      <MeineGeschichte />

      {/* ========== WAS ICH HEUTE TUE ========== */}
      <PaperGradientBG>
        <section className="py-12 md:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="mb-12">
              <SectionBadge label="Fokus" className="mb-2" />
              <h2 className="text-lg md:text-xl font-mono font-bold text-[#EFEBDC]">
                Drei Säulen. Ein Ziel.
              </h2>
            </div>

            {/* Timeline */}
            {pillars.map(({ id, title, icon: Icon, highlight, detail }, i) => (
              <motion.div
                key={id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-80px" }}
                className={[
                  "relative pl-8 md:pl-12 pb-12 last:pb-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[#AC8E66]/40",
                  canOpenServiceDetail(id)
                    ? "cursor-pointer transition-colors hover:bg-[#AC8E66]/5"
                    : "",
                ].join(" ")}
                role={canOpenServiceDetail(id) ? "button" : undefined}
                tabIndex={canOpenServiceDetail(id) ? 0 : undefined}
                aria-label={canOpenServiceDetail(id) ? `${title} im Terminal oeffnen` : undefined}
                onClick={(event) => handleServicePillarClick(id, event)}
                onKeyDown={(e) => {
                  if (!canOpenServiceDetail(id)) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openServiceDetail(id);
                  }
                }}
              >
                {/* Timeline line */}
                {i < pillars.length - 1 && (
                  <div className="absolute left-[11px] md:left-[19px] top-8 bottom-0 w-px bg-[#AC8E66]/20" />
                )}
                {/* Timeline dot with icon */}
                <div className="absolute left-0 md:left-2 top-1 w-6 h-6 rounded-full bg-[#0F0F0F] border-2 border-[#AC8E66]/50 flex items-center justify-center">
                  <Icon className="text-[#AC8E66] text-[10px]" />
                </div>
                {/* Title */}
                <h3 className="text-sm md:text-base text-[#AC8E66] font-semibold mb-3 font-mono">
                  {title}
                </h3>
                {/* Highlight */}
                <p className="text-xs md:text-sm text-[#EFEBDC]/80 leading-relaxed mb-3 font-mono">
                  {highlight}
                </p>
                {/* Detail */}
                <p className="text-xs md:text-sm text-[#EFEBDC]/50 leading-relaxed font-mono">
                  {detail}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      </PaperGradientBG>
      <TerminalDetailOverlay
        isOpen={Boolean(activeServiceDetail)}
        onClose={closeServiceDetail}
        title={activeServicePillar?.title || "Entwicklung"}
        subtitle={activeServiceDetail?.subtitle || "ls services/"}
        lines={activeServiceDetail?.lines || []}
        storageKey={activeServiceDetail?.storageKey}
        ctaLabel={activeServiceDetail?.ctaLabel}
        ctaPath={activeServiceDetail?.ctaPath}
        ctaMode={activeServiceDetail?.ctaMode}
        onPrev={prevServiceDetail}
        onNext={nextServiceDetail}
        hasPrev={activeServiceIndex > 0}
        hasNext={activeServiceIndex >= 0 && activeServiceIndex < orderedServiceIds.length - 1}
      />

      {/* ========== TECH STACK ========== */}
      <PaperGradientBG variant={{ stops: ["#1a1a1a", "#1a1a1a"], angle: 180 }}>
        <section className="py-12 md:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="mb-12">
              <SectionBadge label="Tech Stack" className="mb-2" />
              <h2 className="text-lg md:text-xl font-mono font-bold text-[#EFEBDC]">
                Womit ich arbeite.
              </h2>
            </div>
            <TechStack
              extended
              darkTheme
              enableDetailOverlay
              detailCtaLabel="Saghallo"
              detailCtaMode="inlineContact"
            />
          </div>
        </section>
      </PaperGradientBG>

      {/* ========== REFERENZEN ========== */}
      <PaperGradientBG variant={{ stops: ["#1a1a1a", "#1a1a1a"], angle: 180 }}>
        <section id="referenzen" className="py-12 md:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="mb-12">
              <SectionBadge label="Referenzen" className="mb-2" />
              <h2 className="text-lg md:text-xl font-mono font-bold text-[#EFEBDC]">
                Was andere sagen.
              </h2>
            </div>
            <HowItWorksSmall />
            <div className="mt-8 flex flex-wrap justify-center gap-6 sm:gap-8">
              {trustedCompanies.map((company) => (
                <button
                  key={company.name}
                  type="button"
                  onClick={() => openCompanyDetail(company.name)}
                  aria-label={`${company.name} Referenz oeffnen`}
                  className="relative w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] flex items-center justify-center rounded-full bg-[#1a1a1a]"
                  title={company.name}
                >
                  <TechRoughCircle size={72} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="sm:hidden" />
                  <TechRoughCircle size={80} color="#AC8E66" strokeWidth={1.2} roughness={0.8} className="hidden sm:block" />
                  <img
                    src={company.image}
                    alt={company.name}
                    className={`object-contain opacity-75 relative z-[1] ${
                      company.dark ? "brightness-0 invert" : ""
                    } ${company.large ? "w-11 h-11 sm:w-12 sm:h-12" : "w-7 h-7 sm:w-8 sm:h-8"}`}
                  />
                </button>
              ))}
            </div>
            <TerminalDetailOverlay
              isOpen={Boolean(activeCompanyDetail)}
              onClose={() => setActiveCompanyKey(null)}
              title={activeCompanyDetail?.title || "Referenz"}
              subtitle={activeCompanyDetail?.subtitle || "ls references/"}
              lines={activeCompanyDetail?.lines || []}
              storageKey={activeCompanyDetail?.storageKey}
              ctaLabel="Saghallo"
              ctaMode="inlineContact"
              onPrev={prevCompanyDetail}
              onNext={nextCompanyDetail}
              hasPrev={activeCompanyIndex > 0}
              hasNext={activeCompanyIndex >= 0 && activeCompanyIndex < orderedCompanyKeys.length - 1}
            />
          </div>
        </section>
      </PaperGradientBG>

      {/* ========== KOMPASS ========== */}
      <PaperBG>
        <section id="kompass" className="py-12 md:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="mb-12">
              <SectionBadge label="Kompass" className="mb-2" />
              <h2 className="text-lg md:text-xl font-mono font-bold text-gray-900">
                Woran ich glaube – Prinzipien, die mich leiten.
              </h2>
            </div>
            {/* Timeline */}
            {manifest.map((item, i) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "-80px" }}
                className="relative pl-8 md:pl-12 pb-12 last:pb-0"
              >
                {/* Timeline line */}
                {i < manifest.length - 1 && (
                  <div className="absolute left-[11px] md:left-[19px] top-8 bottom-0 w-px bg-[#AC8E66]/20" />
                )}
                {/* Timeline dot */}
                <div className="absolute left-0 md:left-2 top-1 w-6 h-6 rounded-full bg-[#0F0F0F] border-2 border-[#AC8E66]/50 flex items-center justify-center">
                  <span className="text-[8px] font-mono text-[#AC8E66] font-bold">{item.id}</span>
                </div>
                {/* Title */}
                <h3 className="text-sm md:text-base text-[#AC8E66] font-semibold mb-3 font-mono">
                  {item.title}
                </h3>
                {/* Text */}
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed font-mono">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      </PaperBG>

      {/* ========== CTA ========== */}
      <PaperGradientBG>
        <section className="py-12 md:py-20">
          <div className="max-w-3xl mx-auto px-6">
            <div className="mb-8">
              <SectionBadge label="Kontakt" className="mb-2" />
              <h2 className="text-lg md:text-xl font-mono font-bold text-[#EFEBDC]">
                Klingt interessant? Lass uns reden.
              </h2>
            </div>
            <p className="text-xs md:text-sm text-[#EFEBDC]/60 font-mono mb-8">
              Über dein Projekt, deine Herausforderung oder einfach so.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-16 sm:gap-6">
            
              <MonoButton
                onClick={() => setShowBooking(true)}
                className="px-8 py-3 text-sm w-full sm:w-auto border border-brand hover:shadow-brand"
                textColor="text-brand"
                underlineColors="ZenBrand"
              >
                saghallo, lern mich per Teams kennen
              </MonoButton>
            </div>
            <ContactForm />
          </div>
        </section>
      </PaperGradientBG>

      {/* Modal */}
      {showBooking &&
        ReactDOM.createPortal(
          <ModalWrapper onClose={() => setShowBooking(false)}>
            <BookingModal onClose={() => setShowBooking(false)} />
          </ModalWrapper>,
          document.getElementById("modal-root")
        )
      }

    </>
  );
};

export default About;
